import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff, Lock } from "lucide-react";

const LOGO_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_charger-service-hub-1/artifacts/1jv6mr6c_2.jpeg";

const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || "xstrongpowerservice@gmail.com";

export default function LoginPage() {
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    try {
      await login(ADMIN_EMAIL, password);
      navigate("/");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Incorrect password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-5 py-10"
      style={{
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        background: "linear-gradient(180deg, #f0fdf4 0%, #ffffff 65%)",
      }}
    >
      {/* ── Logo + Brand ── */}
      <div className="text-center mb-8">
        <img
          src={LOGO_URL}
          alt="XSTRONG POWER"
          className="w-36 h-auto mx-auto mb-4 object-contain drop-shadow-sm"
        />
        <h1
          className="text-xl font-black text-gray-900 tracking-tight"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          XSTRONG POWER EV CHARGER
        </h1>
        <p className="text-gray-500 text-sm mt-1">Service Center — Staff Portal</p>
      </div>

      {/* ── Login Card ── */}
      <div className="w-full max-w-sm bg-white rounded-3xl shadow-md border border-gray-100 p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-lime-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm shadow-lime-900/20">
            <Lock className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <h2
            className="text-lg font-bold text-gray-900"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Staff Login
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Password field only */}
          <div>
            <label
              htmlFor="password"
              className="text-sm font-semibold text-gray-700 block mb-1.5"
            >
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPwd ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter staff password"
                required
                data-testid="login-password"
                className="w-full px-4 py-3 border-2 border-lime-300 rounded-2xl text-sm placeholder-gray-400 focus:outline-none focus:border-lime-500 pr-11 bg-white transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPwd(!showPwd)}
                data-testid="toggle-password"
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPwd ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || !password.trim()}
            data-testid="login-submit"
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-lime-500 hover:bg-lime-600 active:bg-lime-700 text-white rounded-2xl font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-lime-900/20"
          >
            <Lock className="w-4 h-4" strokeWidth={2.5} />
            {loading ? "Signing in..." : "Sign In to Staff Portal"}
          </button>
        </form>
      </div>

      {/* ── Footer ── */}
      <p className="text-[11px] text-gray-400 mt-6 font-medium tracking-widest uppercase">
        XSTRONG POWER EV CHARGER — Service Center v1.0
      </p>
    </div>
  );
}
